<template>
  <div class="home-container">
    <div class="bg-wrapper">
      <div class="bg-gradient"></div>
      <div class="bg-float-circle circle-1"></div>
      <div class="bg-float-circle circle-2"></div>
      <div class="bg-float-circle circle-3"></div>
      <div class="bg-float-circle circle-4"></div>
      <div class="bg-particles">
        <div class="particle" v-for="i in 8" :key="i"></div>
      </div>
    </div>
    
    <div class="header-section">
      <div class="header-content">
        <div class="greeting-wrapper animate-fade-down">
          <div class="greeting-time">{{ greetingTime }}</div>
          <div class="user-row">
            <div class="avatar-container" @click="goToProfile">
              <img :src="user.avatar || defaultAvatar" class="avatar" />
              <div class="avatar-glow" :class="'glow-' + user.vip_level"></div>
              <div class="avatar-ring" :class="'ring-' + user.vip_level"></div>
            </div>
            <div class="user-text">
              <h1 class="nickname">{{ user.nickname || '游客' }}</h1>
              <div class="home-bank-vip-list" v-if="homeBankVipLines.length">
                <span
                  v-for="line in homeBankVipLines"
                  :key="line.bank_id"
                  class="home-bank-vip-chip"
                  :class="[line.has_access ? 'vip-' + line.level : 'vip-none']"
                >
                  {{ line.label }}
                </span>
              </div>
              <div v-else class="vip-badge vip-0">
                <van-icon name="user-circle-o" size="12" />
                <span>免费体验</span>
              </div>
            </div>
          </div>
        </div>
        <div class="header-actions animate-fade-down-delay">
          <div class="action-btn" @click="showNotifications">
            <van-icon name="bell" size="22" />
            <van-badge :content="unreadCount > 0 ? unreadCount : ''" class="badge" />
          </div>
        </div>
      </div>
    </div>

    <van-popup v-model="showNotificationPopup" position="right" :style="{ width: '85%', height: '100%' }" :overlay="true" :close-on-click-overlay="true" class="notification-popup-wrapper">
      <div class="notification-popup">
        <div class="notification-header">
          <div class="header-left">
            <h3>消息通知</h3>
            <van-badge :content="unreadCount > 0 ? unreadCount : ''" class="header-badge" />
          </div>
          <div class="notification-header-actions">
            <van-button 
              type="primary" 
              size="small" 
              plain
              @click="markAllAsRead" 
              v-if="unreadCount > 0"
              class="mark-all-btn"
            >
              全部已读
            </van-button>
            <div class="close-btn" @click="showNotificationPopup = false">
              <van-icon name="cross" size="20" />
            </div>
          </div>
        </div>
        
        <div class="notification-scroll">
          <div class="notification-list">
            <div v-if="notifications.length === 0" class="empty-state">
              <div class="empty-icon">
                <van-icon name="inbox" size="48" />
              </div>
              <p class="empty-text">暂无消息</p>
              <p class="empty-hint">有新消息会在这里提醒您</p>
            </div>
            <div
              v-for="(notification, index) in notifications"
              :key="notification.id"
              class="notification-item animate-slide-left"
              :class="{ 'unread': !notification.is_read }"
              :style="{ animationDelay: (index * 0.05) + 's' }"
              @click="handleNotificationClick(notification)"
            >
              <div class="notification-icon">
                <van-icon :name="getNotificationIcon(notification.type)" size="22" />
              </div>
              <div class="notification-body">
                <div class="notification-content">{{ notification.content }}</div>
                <div class="notification-meta">
                  <span class="notification-time">{{ formatNotificationTime(notification.create_time) }}</span>
                  <span v-if="notification.related_answer_id" class="notification-link">查看详情</span>
                </div>
              </div>
              <div class="notification-status" :class="{ 'dot': !notification.is_read }">
                <van-icon v-if="notification.is_read" name="check" size="14" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </van-popup>

    <div class="banner-section animate-slide-up">
      <van-swipe :autoplay="3000" indicator-color="white" class="banner-swipe" @change="onBannerChange">
        <van-swipe-item v-for="banner in banners" :key="banner.id" @click="handleBannerClick(banner)">
          <div class="banner-item" :style="{ backgroundImage: 'url(' + getBannerUrl(banner.image_url) + ')' }">
            <div class="banner-overlay"></div>
            <div class="banner-content">
              <div class="banner-tag">{{ banner.title || '热门活动' }}</div>
            </div>
          </div>
        </van-swipe-item>
        <template #indicator>
          <div class="banner-indicators">
            <div 
              class="indicator-dot" 
              v-for="(banner, index) in banners" 
              :key="index"
              :class="{ active: currentBanner === index }"
            ></div>
          </div>
        </template>
      </van-swipe>
    </div>

    <div class="banks-section animate-slide-up-delay" v-if="banks.length">
      <BankPicker
        :banks="banks"
        :selected-id="selectedBankId"
        :showTitle="false"
        @select="onBankClick"
      />
      <div
        v-show="false"
        v-for="bank in banks"
        :key="bank.id"
        class="bank-card"
        :class="{ locked: !bank.has_access }"
        @click="onBankClick(bank)"
      >
        <div class="bank-icon">{{ bank.icon || '📚' }}</div>
        <div class="bank-info">
          <div class="bank-name">{{ bank.name }}</div>
          <div class="bank-meta">{{ bank.question_count }} 道题 · {{ bank.has_access ? (bank.expire_text || '已开通') : '未开通' }}</div>
        </div>
        <van-icon :name="bank.has_access ? 'arrow' : 'lock'" size="18" class="bank-arrow" />
      </div>
    </div>

    <div class="quick-entry-section animate-slide-up-delay">

      <div class="entry-grid">
        <div class="entry-card small-entry" @click="goToVip" :class="{ 'vip-card': true }">
          <div class="small-entry-icon">👑</div>
          <div class="small-entry-text">
            <span class="small-title">VIP会员</span>
            <span class="small-desc">解锁全部题目</span>
          </div>
        </div>
        <div class="entry-card small-entry" @click="goToResult">
          <div class="small-entry-icon">📊</div>
          <div class="small-entry-text">
            <span class="small-title">答题记录</span>
            <span class="small-desc">查看历史成绩</span>
          </div>
        </div>
        <div
          v-if="shareInviteEnabled"
          class="entry-card small-entry"
          @click="inviteFriends"
        >
          <div class="small-entry-icon">📤</div>
          <div class="small-entry-text">
            <span class="small-title">邀请好友</span>
            <span class="small-desc">分享给微信好友</span>
          </div>
        </div>
      </div>
    </div>

    <div class="stats-showcase animate-slide-up-more">
      <div class="stats-header">
        <h3>我的成就</h3>
        <span class="stats-more" @click="goToResult">全部 <van-icon name="arrow" size="12" /></span>
      </div>
      <div class="stats-card-wrapper">
        <div class="stats-card" v-for="(stat, index) in statsData" :key="stat.label" :style="{ animationDelay: index * 0.1 + 's' }">
          <div class="stats-icon-bg" :style="{ background: stat.bgColor }">
            <van-icon :name="stat.icon" :color="stat.color" size="24" />
          </div>
          <div class="stats-info">
            <div class="stats-value">{{ stat.value }}</div>
            <div class="stats-label">{{ stat.label }}</div>
            <div v-if="stat.extra" class="stats-extra">{{ stat.extra }}</div>
          </div>
          <div class="stats-trend" v-if="stat.trend">
            <van-icon :name="stat.trend > 0 ? 'arrow-up' : 'arrow-down'" :color="stat.trend > 0 ? '#52c41a' : '#ff4d4f'" />
            <span :style="{ color: stat.trend > 0 ? '#52c41a' : '#ff4d4f' }">{{ Math.abs(stat.trend) }}%</span>
          </div>
        </div>
      </div>
    </div>

    <div class="daily-task-section animate-slide-up-more-delay" v-if="dailyTasks.length > 0">
      <div class="task-header">
        <div class="task-title">
          <span class="task-icon">⚡</span>
          <h3>每日任务</h3>
        </div>
        <div class="task-progress">
          <span>{{ completedTasksCount }}/{{ dailyTasks.length }}</span>
          <van-progress :percentage="taskProgress" :show-pivot="false" color="linear-gradient(90deg, #667eea, #764ba2)" />
        </div>
      </div>
      <div 
        class="task-item" 
        v-for="task in dailyTasks" 
        :key="task.id"
        :class="{ completed: task.completed, disabled: task.completed }"
        @click="handleTaskClick(task)"
      >
        <div class="task-check">
          <van-icon 
            :name="task.completed ? 'checked' : 'circle'" 
            :color="task.completed ? '#52c41a' : '#ccc'" 
            size="20" 
          />
        </div>
        <div class="task-content">
          <span class="task-name" :class="{ completed: task.completed }">{{ task.name }}</span>
          <span class="task-reward" :class="{ completed: task.completed }">{{ task.reward }}</span>
          <span v-if="task.progress_hint" class="task-progress-hint">{{ task.progress_hint }}</span>
        </div>
        <div class="task-btn" :class="{ completed: task.completed }">
          {{ task.actionLabel }}
        </div>
      </div>
    </div>

    <div class="bottom-safe-area"></div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { Toast } from 'vant'
import { fetchShareConfigPublic, isShareInviteEnabled, inviteShareFriends } from '@/utils/shareInvite'
import BankPicker from '@/components/BankPicker.vue'
import { formatBankVipDaysText } from '@/utils/vipDisplay'

export default {
  name: 'Home',
  components: { BankPicker },
  data() {
    return {
      defaultAvatar: 'https://img.yzcdn.cn/vant/cat.jpeg',
      banners: [],
      showNotificationPopup: false,
      notifications: [],
      unreadCount: 0,
      notificationTimer: null,
      currentBanner: 0,
      dailyTasks: [],
      shareInviteEnabled: false,
      banks: [],
      selectedBankId: parseInt(localStorage.getItem('last_bank_id'), 10) || 0,
      maxOwnedLevel: 0
    }
  },
  computed: {
    ...mapGetters(['isVip']),
    user() {
      return this.$store.state.user || {}
    },
    greetingTime() {
      const hour = new Date().getHours()
      if (hour < 6) return '凌晨好'
      if (hour < 9) return '早上好'
      if (hour < 12) return '上午好'
      if (hour < 14) return '中午好'
      if (hour < 18) return '下午好'
      if (hour < 22) return '晚上好'
      return '夜深了'
    },
    questionsCount() {
      const categories = this.$store.state.categories || []
      let total = 0
      for (const category of categories) {
        for (const sub of category.sub_categories || []) {
          total += sub.question_count || 0
        }
      }
      return total
    },
    homeBankVipLines() {
      const fromUser = this.user.bank_vips || []
      if (fromUser.length) {
        return fromUser.map((bv) => {
          const bankName = bv.bank_name || `题库${bv.bank_id}`
          const hasAccess = !!bv.has_access
          const level = parseInt(bv.package_level, 10) || 0
          let suffix = '未开通'
          if (hasAccess) {
            const days = formatBankVipDaysText(bv)
            suffix = bv.package_name ? `${bv.package_name} · ${days}` : days
          }
          return {
            bank_id: bv.bank_id,
            has_access: hasAccess,
            level,
            label: `${bankName}：${suffix}`
          }
        })
      }
      return (this.banks || []).map((bank) => {
        const hasAccess = !!bank.has_access
        const level = parseInt(bank.entitlement_package_level, 10) || 0
        let suffix = '未开通'
        if (hasAccess) {
          const pkg = bank.entitlement_package_name || '已开通'
          const exp = bank.expire_text || ''
          suffix = exp ? `${pkg} · ${exp}` : pkg
        }
        return {
          bank_id: bank.id,
          has_access: hasAccess,
          level,
          label: `${bank.name}：${suffix}`
        }
      })
    },
    statsData() {
      return [
        { 
          label: '答题次数', 
          value: this.user.answer_count || 0, 
          icon: 'edit',
          color: '#667eea',
          bgColor: 'linear-gradient(135deg, #e8f0fe 0%, #d4e4fd 100%)',
          trend: 12
        },
        { 
          label: '累计得分', 
          value: this.user.total_score || 0, 
          icon: 'todo-list-o',
          color: '#fa8c16',
          bgColor: 'linear-gradient(135deg, #fff7e6 0%, #ffeeba 100%)',
          trend: 8,
          extra: '任务积分 ' + (this.user.points != null ? this.user.points : 0)
        },
        { 
          label: '正确率', 
          value: (this.user.accuracy_percent || 0) + '%', 
          icon: 'aim',
          color: '#52c41a',
          bgColor: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
          trend: -2
        }
      ]
    },
    completedTasksCount() {
      return this.dailyTasks.filter(task => task.completed).length
    },
    taskProgress() {
      if (this.dailyTasks.length === 0) return 0
      return Math.round((this.completedTasksCount / this.dailyTasks.length) * 100)
    }
  },
  mounted() {
    this.loadUserInfo()
    this.loadCategories()
    this.loadBanks()
    this.loadBanners()
    this.loadShareInviteConfig()
    this.loadNotifications()
    this.loadDailyTasks()
    this.notificationTimer = setInterval(() => {
      this.loadNotifications()
    }, 30000)
  },
  activated() {
    if (this.$store.state.token) {
      this.loadUserInfo()
      this.loadBanks()
      this.loadDailyTasks()
      this.loadShareInviteConfig()
    }
  },
  beforeDestroy() {
    if (this.notificationTimer) {
      clearInterval(this.notificationTimer)
    }
  },
  methods: {
    async loadUserInfo() {
      try {
        await this.$store.dispatch('fetchUserInfo')
      } catch (err) {
        console.error(err)
      }
    },
    async loadCategories() {
      try {
        await this.$store.dispatch('fetchCategories')
      } catch (err) {
        console.error('加载分类失败:', err)
      }
    },
    async loadShareInviteConfig() {
      try {
        const cfg = await fetchShareConfigPublic(false)
        this.shareInviteEnabled = isShareInviteEnabled(cfg)
      } catch (err) {
        this.shareInviteEnabled = false
      }
    },
    inviteFriends() {
      inviteShareFriends()
    },
    async loadBanners() {
      try {
        const response = await this.$http.get('/api/banners')
        if (response.data.code === 200) {
          const responseData = response.data.data || {}
          this.banners = responseData.banners || []
        }
      } catch (err) {
        console.error(err)
      }
    },
    async loadDailyTasks() {
      if (!this.$store.state.token) {
        this.dailyTasks = []
        return
      }
      try {
        const response = await this.$http.get('/api/daily-tasks')
        if (response.data.code !== 200) {
          return
        }
        const responseData = response.data.data || {}
        const tasks = responseData.tasks || []
        this.dailyTasks = tasks.map(t => {
          const kind = t.task_kind || (t.trigger_type === 'manual' ? 'signin_streak' : 'answer_count')
          const cur = t.progress_current != null ? t.progress_current : 0
          const tgt = t.progress_target != null ? t.progress_target : 1
          const hint = kind === 'signin_streak'
            ? ('连续签到 ' + cur + '/' + tgt + ' 天')
            : ('今日答题 ' + cur + '/' + tgt + ' 题')
          return {
            id: t.id,
            name: t.title,
            reward: '+' + t.points + '积分',
            points: t.points,
            completed: !!t.completed,
            task_kind: kind,
            progress_hint: hint,
            actionLabel: t.completed ? '已完成' : (kind === 'signin_streak' ? '去签到' : '去完成')
          }
        })
      } catch (err) {
        this.dailyTasks = []
        console.error('加载每日任务失败:', err)
      }
    },
    async loadNotifications() {
      try {
        const response = await this.$http.get('/api/notifications')
        if (response.data.code === 200) {
          const responseData = response.data.data || {}
          this.notifications = responseData.notifications || []
          this.unreadCount = this.notifications.filter(n => !n.is_read).length
        }
      } catch (err) {
        console.error('加载通知失败:', err)
      }
    },
    showNotifications() {
      this.showNotificationPopup = true
      this.loadNotifications()
    },
    async handleNotificationClick(notification) {
      try {
        await this.$http.post(`/api/notifications/${notification.id}/read`)
        notification.is_read = true
        this.unreadCount = this.notifications.filter(n => !n.is_read).length
        
        if (notification.related_answer_id) {
          this.$router.push(`/record/${notification.related_answer_id}`)
        }
      } catch (err) {
        console.error(err)
      }
    },
    async markAllAsRead() {
      try {
        await this.$http.post('/api/notifications/read-all')
        this.notifications.forEach(n => n.is_read = true)
        this.unreadCount = 0
      } catch (err) {
        console.error(err)
      }
    },
    formatNotificationTime(timestamp) {
      const date = new Date(timestamp * 1000)
      const now = new Date()
      const diff = now - date
      
      if (diff < 60000) return '刚刚'
      if (diff < 3600000) return Math.floor(diff / 60000) + '分钟前'
      if (diff < 86400000) return Math.floor(diff / 3600000) + '小时前'
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const day = date.getDate().toString().padStart(2, '0')
      return `${month}-${day}`
    },
    getNotificationIcon(type) {
      const icons = {
        'answer': 'message-o',
        'system': 'info-o',
        'vip': 'gem-o',
        'score': 'trophy',
        'task': 'calendar-o'
      }
      return icons[type] || 'bell'
    },
    getBannerUrl(url) {
      if (!url) return ''
      if (url.startsWith('http://') || url.startsWith('https://')) {
        return url
      }
      return (process.env.VUE_APP_API_BASE_URL || 'http://localhost:8000') + url
    },
    onBannerChange(index) {
      this.currentBanner = index
    },
    handleBannerClick(banner) {
      if (banner.link_url) {
        this.$router.push(banner.link_url)
      }
    },
    async loadBanks() {
      try {
        if (this.$store.state.token) {
          await this.$store.dispatch('fetchUserInfo')
        }
        const data = await this.$store.dispatch('fetchQuestionBanks')
        this.banks = data.banks || []
        this.maxOwnedLevel = data.maxOwnedLevel || 0
      } catch (e) {
        console.error(e)
      }
    },
    onBankClick(bank) {
      if (!bank) return
      this.selectedBankId = bank.id
      localStorage.setItem('last_bank_id', String(bank.id))
      if (bank.purchase_blocked) {
        Toast('您已开通更高等级题库，无需购买本题库')
        return
      }
      this.$router.push({ path: '/training', query: { bank_id: bank.id, bank_name: bank.name } })
    },
    handleTaskClick(task) {
      if (task.completed) {
        return
      }
      if (task.task_kind === 'signin_streak') {
        this.dailyTaskSignin()
        return
      }
      const lastId = parseInt(localStorage.getItem('last_bank_id'), 10) || 0
      if (lastId > 0) {
        this.$router.push({ path: '/training', query: { bank_id: lastId } })
      } else {
        this.$router.push('/quiz')
      }
    },
    async dailyTaskSignin() {
      try {
        const res = await this.$http.post('/api/daily-tasks/signin')
        Toast(res.data.msg || '签到成功')
        await this.loadDailyTasks()
        await this.$store.dispatch('fetchUserInfo')
      } catch (err) {
        console.error(err)
      }
    },
    goToVip() {
      const lastId = parseInt(localStorage.getItem('last_bank_id'), 10) || 0
      const bank = this.banks.find(b => b.id === lastId) || this.banks[0]
      if (bank) {
        this.$router.push({ path: '/vip', query: { bank_id: bank.id, bank_name: bank.name } })
      } else {
        this.$router.push('/vip')
      }
    },
    goToResult() {
      this.$router.push('/records')
    },
    goToProfile() {
      this.$router.push('/profile')
    }
  }
}
</script>

<style scoped>
.home-container {
  min-height: 100vh;
  background: #f5f6fa;
  padding-bottom: calc(128px + env(safe-area-inset-bottom));
  position: relative;
  overflow-x: hidden;
}

.bg-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 320px;
  z-index: 0;
  overflow: hidden;
}

.bg-gradient {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(180deg, #667eea 0%, #764ba2 100%);
  border-radius: 0 0 40px 40px;
}

.bg-float-circle {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
  animation: float-slow 8s ease-in-out infinite;
}

.circle-1 {
  width: 150px;
  height: 150px;
  top: 30px;
  right: -40px;
  animation-delay: 0s;
}

.circle-2 {
  width: 100px;
  height: 100px;
  top: 120px;
  left: -30px;
  animation-delay: 2s;
}

.circle-3 {
  width: 80px;
  height: 80px;
  bottom: 80px;
  right: 60px;
  animation-delay: 4s;
}

.circle-4 {
  width: 60px;
  height: 60px;
  bottom: 40px;
  left: 40px;
  animation-delay: 1s;
}

.bg-particles {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.particle {
  position: absolute;
  width: 6px;
  height: 6px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  animation: particle-float 6s ease-in-out infinite;
}

.particle:nth-child(1) { top: 20%; left: 10%; animation-delay: 0s; }
.particle:nth-child(2) { top: 30%; left: 25%; animation-delay: 0.5s; }
.particle:nth-child(3) { top: 15%; left: 40%; animation-delay: 1s; }
.particle:nth-child(4) { top: 40%; left: 60%; animation-delay: 1.5s; }
.particle:nth-child(5) { top: 25%; left: 75%; animation-delay: 2s; }
.particle:nth-child(6) { top: 50%; left: 85%; animation-delay: 2.5s; }
.particle:nth-child(7) { top: 35%; left: 50%; animation-delay: 3s; }
.particle:nth-child(8) { top: 55%; left: 30%; animation-delay: 3.5s; }

@keyframes float-slow {
  0%, 100% {
    transform: translateY(0) scale(1);
    opacity: 0.4;
  }
  50% {
    transform: translateY(-30px) scale(1.1);
    opacity: 0.7;
  }
}

@keyframes particle-float {
  0%, 100% {
    transform: translateY(0) scale(1);
    opacity: 0.3;
  }
  50% {
    transform: translateY(-15px) scale(1.2);
    opacity: 0.6;
  }
}

.header-section {
  position: relative;
  z-index: 10;
  padding: calc(16px + env(safe-area-inset-top)) 20px 0;
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.animate-fade-down {
  animation: fadeDown 0.6s ease-out;
}

.animate-fade-down-delay {
  animation: fadeDown 0.6s ease-out 0.15s both;
}

@keyframes fadeDown {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.greeting-time {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 8px;
}

.user-row {
  display: flex;
  align-items: center;
  gap: 14px;
}

.avatar-container {
  position: relative;
  width: 56px;
  height: 56px;
}

.avatar {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  object-fit: cover;
}

.avatar-glow {
  position: absolute;
  top: -6px;
  left: -6px;
  right: -6px;
  bottom: -6px;
  border-radius: 50%;
  animation: glow-pulse 2s ease-in-out infinite;
}

.glow-0 { background: transparent; }
.glow-1 { background: radial-gradient(circle, rgba(24, 144, 255, 0.3), transparent 70%); }
.glow-2 { background: radial-gradient(circle, rgba(47, 84, 235, 0.3), transparent 70%); }
.glow-3 { background: radial-gradient(circle, rgba(114, 46, 209, 0.3), transparent 70%); }
.glow-4 { background: radial-gradient(circle, rgba(250, 140, 22, 0.4), transparent 70%); }

.avatar-ring {
  position: absolute;
  top: -8px;
  left: -8px;
  right: -8px;
  bottom: -8px;
  border-radius: 50%;
  border: 2px solid transparent;
  animation: ring-expand 2s ease-in-out infinite;
}

.ring-0 { display: none; }
.ring-1 { border-color: #1890ff; }
.ring-2 { border-color: #2f54eb; }
.ring-3 { border-color: #722ed1; }
.ring-4 { border-color: #fa8c16; }

@keyframes glow-pulse {
  0%, 100% { opacity: 0.5; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.05); }
}

@keyframes ring-expand {
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.1); }
}

.user-text {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.nickname {
  font-size: 24px;
  font-weight: 700;
  color: white;
  margin: 0;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.vip-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  color: white;
  width: fit-content;
}

.vip-badge.vip-0 { background: linear-gradient(135deg, #52c41a, #389e0d); }
.vip-badge.vip-1 { background: linear-gradient(135deg, #1890ff, #096dd9); }
.vip-badge.vip-2 { background: linear-gradient(135deg, #2f54eb, #1d39c4); }
.vip-badge.vip-3 { background: linear-gradient(135deg, #722ed1, #531dab); }
.vip-badge.vip-4 { background: linear-gradient(135deg, #fa8c16, #d46b08); }

.home-bank-vip-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 6px;
  max-width: 220px;
}

.home-bank-vip-chip {
  display: inline-block;
  font-size: 10px;
  line-height: 1.35;
  padding: 3px 8px;
  border-radius: 8px;
  color: #fff;
  font-weight: 500;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.home-bank-vip-chip.vip-none {
  background: rgba(255, 255, 255, 0.35);
  color: rgba(255, 255, 255, 0.95);
  border: 1px solid rgba(255, 255, 255, 0.45);
}

.home-bank-vip-chip.vip-1 { background: linear-gradient(135deg, #1890ff, #096dd9); }
.home-bank-vip-chip.vip-2 { background: linear-gradient(135deg, #2f54eb, #1d39c4); }
.home-bank-vip-chip.vip-3 { background: linear-gradient(135deg, #722ed1, #531dab); }
.home-bank-vip-chip.vip-4 { background: linear-gradient(135deg, #fa8c16, #d46b08); }

.header-actions {
  display: flex;
  gap: 12px;
  padding-top: 8px;
}

.action-btn {
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  backdrop-filter: blur(10px);
  position: relative;
  transition: all 0.3s ease;
}

.action-btn:active {
  transform: scale(0.9);
  background: rgba(255, 255, 255, 0.3);
}

.badge {
  position: absolute;
  top: -4px;
  right: -4px;
}

.banner-section {
  position: relative;
  z-index: 10;
  margin: 20px 16px 0;
}

.animate-slide-up {
  animation: slideUp 0.6s ease-out 0.2s both;
}

.animate-slide-up-delay {
  animation: slideUp 0.6s ease-out 0.35s both;
}

.animate-slide-up-more {
  animation: slideUp 0.6s ease-out 0.5s both;
}

.animate-slide-up-more-delay {
  animation: slideUp 0.6s ease-out 0.65s both;
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(40px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.banner-swipe {
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 12px 40px rgba(102, 126, 234, 0.3);
}

.banner-item {
  width: 100%;
  height: 170px;
  background-size: cover;
  background-position: center;
  position: relative;
}

.banner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
}

.banner-content {
  position: absolute;
  bottom: 16px;
  left: 16px;
}

.banner-tag {
  background: rgba(255, 255, 255, 0.9);
  color: #667eea;
  padding: 6px 14px;
  border-radius: 16px;
  font-size: 13px;
  font-weight: 600;
}

.banner-indicators {
  position: absolute;
  bottom: 12px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 6px;
}

.indicator-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.4);
  transition: all 0.3s ease;
}

.indicator-dot.active {
  width: 20px;
  border-radius: 4px;
  background: white;
}

.banks-section {
  margin: 40px 16px 20px;
}

.banks-section .section-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  margin-bottom: 12px;
}

.banks-section .section-head h3 {
  font-size: 17px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0;
}

.banks-section .section-hint {
  font-size: 12px;
  color: #8c8c8c;
}

.bank-card {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  margin-bottom: 10px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(102, 126, 234, 0.12);
}

.bank-card.locked {
  opacity: 0.92;
  background: #fafafa;
}

.bank-icon {
  font-size: 32px;
  line-height: 1;
}

.bank-name {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
}

.bank-meta {
  font-size: 12px;
  color: #8c8c8c;
  margin-top: 4px;
}

.bank-arrow {
  margin-left: auto;
  color: #667eea;
}

.quick-entry-section {
  position: relative;
  z-index: 10;
  padding: 20px 16px 0;
}

.entry-card {
  background: white;
  border-radius: 20px;
  overflow: hidden;
  transition: all 0.3s ease;
}

.entry-card:active {
  transform: scale(0.98);
}

.main-entry {
  padding: 24px;
  margin-bottom: 12px;
  position: relative;
  box-shadow: 0 8px 32px rgba(102, 126, 234, 0.15);
}

.entry-decoration {
  position: absolute;
  top: 0;
  right: 0;
  width: 140px;
  height: 140px;
  overflow: hidden;
}

.deco-circle {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.15), rgba(118, 75, 162, 0.1));
  animation: deco-float 4s ease-in-out infinite;
}

.dc-1 { width: 100px; height: 100px; top: -30px; right: -30px; }
.dc-2 { width: 70px; height: 70px; top: 20px; right: 20px; animation-delay: 1s; }
.dc-3 { width: 50px; height: 50px; bottom: -10px; right: 60px; animation-delay: 2s; }

@keyframes deco-float {
  0%, 100% { transform: translateY(0) scale(1); }
  50% { transform: translateY(-10px) scale(1.05); }
}

.entry-main-content {
  display: flex;
  align-items: center;
  gap: 16px;
  position: relative;
  z-index: 1;
}

.entry-icon-wrap {
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
}

.bounce-animation {
  animation: bounce-gentle 2s ease-in-out infinite;
}

@keyframes bounce-gentle {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}

.entry-icon {
  font-size: 32px;
  color: #ffffff;
}

.entry-text h2 {
  font-size: 20px;
  font-weight: 700;
  color: #1a1a2e;
  margin: 0 0 6px 0;
}

.entry-text p {
  font-size: 14px;
  color: #8c8c8c;
  margin: 0;
}

.entry-action {
  position: absolute;
  right: 24px;
  top: 50%;
  transform: translateY(-50%);
}

.play-icon {
  color: #667eea;
  animation: pulse-play 1.5s ease-in-out infinite;
}

@keyframes pulse-play {
  0%, 100% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.8; }
}

.entry-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
}

.small-entry {
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
}

.small-entry.vip-card {
  background: linear-gradient(135deg, #fa8c16, #d46b08);
  box-shadow: 0 4px 16px rgba(250, 140, 22, 0.3);
}

.small-entry-icon {
  font-size: 28px;
}

.small-entry-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.small-title {
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
}

.small-entry.vip-card .small-title {
  color: white;
}

.small-desc {
  font-size: 12px;
  color: #8c8c8c;
}

.small-entry.vip-card .small-desc {
  color: rgba(255, 255, 255, 0.85);
}

.stats-showcase {
  position: relative;
  z-index: 10;
  padding: 24px 16px 0;
}

.stats-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.stats-header h3 {
  font-size: 17px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
}

.stats-more {
  font-size: 13px;
  color: #999;
  display: flex;
  align-items: center;
  gap: 2px;
}

.stats-card-wrapper {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}

.stats-card {
  background: white;
  border-radius: 16px;
  padding: 16px 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  animation: stats-pop 0.5s ease-out both;
  transition: all 0.3s ease;
}

.stats-card:active {
  transform: scale(0.95);
}

@keyframes stats-pop {
  from {
    opacity: 0;
    transform: scale(0.8);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.stats-icon-bg {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.stats-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.stats-value {
  font-size: 22px;
  font-weight: 700;
  color: #1a1a2e;
}

.stats-label {
  font-size: 12px;
  color: #8c8c8c;
}

.stats-extra {
  font-size: 11px;
  color: #667eea;
  margin-top: 4px;
  font-weight: 500;
}

.stats-trend {
  display: flex;
  align-items: center;
  gap: 2px;
  font-size: 12px;
  font-weight: 500;
}

.daily-task-section {
  position: relative;
  z-index: 10;
  padding: 24px 16px 0;
}

.task-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
}

.task-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.task-icon {
  font-size: 20px;
}

.task-title h3 {
  font-size: 17px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0;
}

.task-progress {
  display: flex;
  align-items: center;
  gap: 10px;
}

.task-progress span {
  font-size: 13px;
  color: #667eea;
  font-weight: 600;
}

.task-progress .van-progress {
  width: 80px;
}

.task-item {
  background: white;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  cursor: pointer;
  margin-bottom: 12px;
}

.task-item:active {
  transform: scale(0.98);
}

.task-check {
  width: 28px;
  height: 28px;
  background: linear-gradient(135deg, #e8f0fe, #d4e4fd);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.task-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.task-name {
  font-size: 15px;
  font-weight: 500;
  color: #1a1a2e;
}

.task-reward {
  font-size: 12px;
  color: #667eea;
  font-weight: 500;
}

.task-progress-hint {
  font-size: 11px;
  color: #8c8c8c;
  margin-top: 2px;
}

.task-btn {
  padding: 6px 14px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  transition: all 0.3s ease;
}

.task-btn.completed {
  background: #f0f0f0;
  color: #999;
  box-shadow: none;
}

.task-item.completed .task-check {
  background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
}

.task-item.completed .task-name {
  color: #999;
}

.task-item.completed .task-reward {
  color: #bbb;
}

.task-item.disabled {
  pointer-events: none;
  opacity: 0.7;
}

.bottom-safe-area {
  height: 20px;
}

.notification-popup-wrapper {
  background: transparent;
}

.notification-popup {
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #f5f6fa;
}

.notification-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: calc(20px + env(safe-area-inset-top)) 20px 16px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 0 0 24px 24px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.notification-header h3 {
  font-size: 18px;
  font-weight: 700;
  color: white;
  margin: 0;
}

.header-badge {
  background: rgba(255, 255, 255, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.5);
}

.notification-header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.mark-all-btn {
  background: rgba(255, 255, 255, 0.2) !important;
  border-color: rgba(255, 255, 255, 0.4) !important;
  color: white !important;
  font-size: 13px;
  padding: 6px 14px;
}

.close-btn {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  color: white;
}

.notification-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
}

.notification-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  text-align: center;
}

.empty-icon {
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #e8ecf0 0%, #d0d7de 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  color: #999;
}

.empty-text {
  font-size: 16px;
  font-weight: 600;
  color: #1a1a2e;
  margin: 0 0 8px 0;
}

.empty-hint {
  font-size: 13px;
  color: #999;
  margin: 0;
}

.notification-item {
  background: white;
  border-radius: 16px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: 0;
}

.notification-item:active {
  transform: scale(0.98);
}

.notification-item.unread {
  background: linear-gradient(135deg, #fff7f0 0%, #fff0e6 100%);
  border-left: 3px solid #fa8c16;
}

.animate-slide-left {
  animation: slideLeft 0.4s ease-out forwards;
}

@keyframes slideLeft {
  from {
    opacity: 0;
    transform: translateX(30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

.notification-icon {
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  flex-shrink: 0;
}

.notification-item.unread .notification-icon {
  background: linear-gradient(135deg, #fa8c16 0%, #d46b08 100%);
}

.notification-body {
  flex: 1;
  min-width: 0;
}

.notification-content {
  font-size: 14px;
  color: #1a1a2e;
  line-height: 1.5;
  margin-bottom: 8px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-meta {
  display: flex;
  align-items: center;
  gap: 12px;
}

.notification-time {
  font-size: 12px;
  color: #999;
}

.notification-link {
  font-size: 12px;
  color: #667eea;
  font-weight: 500;
}

.notification-status {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #999;
}

.notification-status.dot {
  width: 8px;
  height: 8px;
  background: #fa8c16;
  border-radius: 50%;
  box-shadow: 0 0 8px rgba(250, 140, 22, 0.5);
}

@media screen and (max-width: 360px) {
  .stats-card-wrapper {
    gap: 8px;
  }
  
  .stats-value {
    font-size: 18px;
  }
}
</style>